import type { CompletePrice, Price } from '../api/models/bff/Price';
import type { RawReceiptResponse, ReceiptResponse } from '../api/models/bff/Receipt';
import { isObject } from '../utils/objectUtils';
import { isNonEmptyString } from '../utils/stringUtils';
import { parseAmount } from '../utils/numberUtils';

/**
 * Coerces a raw receipt to the documented contract so consumers never deal with
 * off-contract shapes:
 * - `discount` is kept only when it holds a non-zero `net` amount; empty, malformed
 *   and zero-amount values become `undefined` (contract: absent when no discount applies).
 * - a missing `subtotal` falls back to `total` (their equality is the no-discount invariant).
 */
export function normalizeReceipt(raw: RawReceiptResponse): ReceiptResponse {
  const { discount, subtotal, ...receipt } = raw;

  return {
    ...receipt,
    subtotal: subtotal ?? raw.total,
    discount: normalizeDiscount(discount),
  };
}

function normalizeDiscount(discount: unknown): CompletePrice | undefined {
  if (!isCompletePrice(discount)) return undefined;

  return parseAmount(discount.net.amount) ? discount : undefined;
}

function isCompletePrice(value: unknown): value is CompletePrice {
  if (!isObject(value)) return false;

  const { net, gross } = value as Partial<CompletePrice>;
  return isPrice(net) && isPrice(gross);
}

function isPrice(value: unknown): value is Price {
  if (!isObject(value)) return false;

  const { amount, currency } = value as Partial<Price>;
  return isNonEmptyString(amount) && isNonEmptyString(currency);
}
